import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'compliance-grc',
  slug: 'compliance-grc',
  name: 'Compliance & GRC',
  description: 'Self-reported compliance posture tracking across seven frameworks (SOC 2, ISO 27001, HIPAA, PCI, FERPA/COPPA, NIST CSF 2.0, CIS), plus the records Nexus keeps for privacy/GRC operations: DPAs, consent, disclosure logging, retention policy, and evidence snapshots. This is posture tracking and internal record-keeping, not a certification — a control marked "met" here reflects what someone in your organization assessed and recorded, not a third-party audit finding.',
  icon: 'GppGood',
  endpoints: [
    {
      slug: 'framework-posture',
      method: 'GET',
      path: '/api/compliance/:fwid',
      summary: 'Get one framework\'s control posture',
      description: 'Returns the full control tree for one framework, grouped the same way the framework\'s catalog defines it. Each control carries a manual status (met/partial/not/na, or unassessed if never touched), any auto-derived suggestion from live Suite signals (e.g. MFA coverage, patch compliance) that a human hasn\'t yet overridden, notes, owner, due date, and attached evidence file metadata. Returns 404 for an unknown framework id.',
      auth: 'session',
      params: [{ name: 'fwid', in: 'path', type: 'string', required: true, description: 'Framework id, e.g. nist-csf, soc2, iso27001, hipaa, pci, ferpa, cis.' }],
      responseExample: `{
  "id": "nist-csf",
  "name": "NIST CSF 2.0",
  "score": 71,
  "counts": { "met": 12, "partial": 4, "not": 3, "na": 1, "unassessed": 2, "total": 22 },
  "groups": [
    {
      "id": "PR",
      "name": "Protect",
      "controls": [
        {
          "id": "PR.AA-03",
          "title": "Multi-factor authentication is enforced",
          "group": "Protect",
          "status": "met",
          "auto": { "status": "met", "detail": "MFA required for 18/18 admin accounts" },
          "effective": "met",
          "notes": "Enforced tenant-wide since 2026-02.",
          "owner": "M. Cunningham",
          "due": "",
          "attachments": [],
          "updatedAt": "2026-06-04T18:20:00.000Z",
          "updatedBy": "mcunningham"
        }
      ]
    }
  ]
}`,
      sourceFile: 'routes/compliance.js, lib/compliance-score.js, lib/compliance-catalog.js',
    },
    {
      slug: 'update-control-status',
      method: 'PUT',
      path: '/api/compliance/:fwid/:cid',
      summary: 'Update a control\'s assessment',
      description: 'Sets one control\'s manual status (met/partial/not/na — any other value is rejected and the status is left unset), notes, owner, and due date. Returns 404 if the framework or control id is unknown. This is the manual override: once a human sets a status here, it wins over any auto-derived suggestion for that control until cleared. Evidence files are attached separately via POST /api/compliance/:fwid/:cid/evidence.',
      auth: 'session',
      params: [
        { name: 'fwid', in: 'path', type: 'string', required: true, description: 'Framework id.' },
        { name: 'cid', in: 'path', type: 'string', required: true, description: 'Control id within the framework, e.g. PR.AA-03.' },
      ],
      requestBodyExample: `{
  "status": "partial",
  "notes": "MFA enforced for staff; still rolling out to shared lab logins.",
  "owner": "M. Cunningham",
  "due": "2026-09-01"
}`,
      responseExample: `{ "ok": true }`,
      sourceFile: 'routes/compliance.js, lib/compliance-score.js',
    },
    {
      slug: 'coverage',
      method: 'GET',
      path: '/api/compliance/coverage',
      summary: 'Get cross-framework control coverage summary',
      description: 'Register-backed view (separate from the per-framework posture above): derives, for every framework the org tracks, how many of the mapped controls in the central control register are implemented/partial/planned/not-applicable, plus a percent-covered score per framework. Read-only; reflects the register\'s own status values, which are independent of the manual met/partial/not/na statuses set via PUT /api/compliance/:fwid/:cid.',
      auth: 'session',
      responseExample: `{
  "frameworks": [
    { "id": "soc2", "name": "SOC 2 Type II" },
    { "id": "ferpa", "name": "FERPA / COPPA" }
  ],
  "summary": [
    { "id": "soc2", "name": "SOC 2 Type II", "total": 64, "implemented": 41, "partial": 12, "planned": 11, "pct": 68 },
    { "id": "ferpa", "name": "FERPA / COPPA", "total": 28, "implemented": 22, "partial": 4, "planned": 2, "pct": 86 }
  ]
}`,
      sourceFile: 'routes/compliance.js, lib/compliance-register.js',
    },
    {
      slug: 'compliance-reports',
      method: 'GET',
      path: '/api/compliance-reports',
      summary: 'List compliance snapshot reports',
      description: 'Lists point-in-time compliance snapshot reports (title, who generated it, when, and the overall posture score), newest first. A new snapshot is generated with POST /api/compliance-reports/generate (captures the current state of every framework and stores it immutably under an id), and a generated report can be exported as a branded PDF via GET /api/compliance-reports/:id/pdf. This list endpoint only returns the summary fields — fetch GET /api/compliance-reports/:id for the full per-framework gap detail.',
      auth: 'session',
      responseExample: `[
  { "id": "rpt_9c41", "title": "Q3 2026 compliance assessment", "createdAt": "2026-07-01T12:00:00.000Z", "createdBy": "mcunningham", "overall": 74 },
  { "id": "rpt_7a02", "title": "Compliance assessment 2026-04-01", "createdAt": "2026-04-01T12:00:00.000Z", "createdBy": "mcunningham", "overall": 66 }
]`,
      sourceFile: 'routes/compliance.js, lib/compliance-score.js',
    },
    {
      slug: 'dpa-records',
      method: 'GET',
      path: '/api/dpa',
      summary: 'List data processing agreements',
      description: 'Lists the tenant\'s vendor/sub-processor Data Processing Agreement register: vendor, purpose, data categories handled, whether it\'s a sub-processor, whether a DPA is signed, and review dates. Supports ?status=, ?subProcessor=true, and ?q= filters. Records are created with POST /api/dpa, edited with PUT /api/dpa/:id, and removed with DELETE /api/dpa/:id — all admin-only. A companion GET /api/dpa/summary surfaces the compliance gap (sub-processors with no signed DPA) as counts.',
      auth: 'session',
      responseExample: `[
  {
    "id": "dpa_3f10",
    "vendor": "Clever Inc.",
    "purpose": "SSO rostering for student accounts",
    "dataCategories": "Student name, email, grade level, class roster",
    "subProcessor": true,
    "dpaSigned": true,
    "dpaUrl": "https://clever.com/legal/dpa",
    "signedDate": "2025-08-01",
    "reviewDate": "2026-08-01",
    "status": "active",
    "notes": "Annual review scheduled for August.",
    "createdAt": "2025-08-01T10:00:00.000Z",
    "updatedAt": "2025-08-01T10:00:00.000Z"
  }
]`,
      sourceFile: 'routes/dpa.js',
    },
    {
      slug: 'consent-ledger',
      method: 'GET',
      path: '/api/consents',
      summary: 'List FERPA/COPPA consent records',
      description: 'Returns the append-only consent ledger recording the written-consent basis for collecting or disclosing student PII — either a school-level authorization (basis "school", studentId null) or a parent\'s signed consent for one student (basis "parent"). Supports ?studentId= and ?activeOnly= filters. New consent events are recorded with POST /api/consents; withdrawal is a soft revoke via POST /api/consents/:id/revoke — rows are never deleted, only marked revoked.',
      auth: 'session',
      params: [
        { name: 'studentId', in: 'query', type: 'string', required: false, description: 'Filter to consent records for one student.' },
        { name: 'activeOnly', in: 'query', type: 'boolean', required: false, description: 'If set, excludes revoked records.' },
      ],
      responseExample: `[
  {
    "id": "cst_a410",
    "studentId": "stu_2291",
    "basis": "parent",
    "scope": "Directory information release for yearbook photos",
    "noticeVersion": "2026-v2",
    "signedBy": "Wei Zhang (parent/guardian)",
    "signedDate": "2026-07-10",
    "evidenceAttachmentId": null,
    "recordedBy": "front.office",
    "revokedAt": null,
    "revokedBy": null,
    "createdAt": "2026-07-10T15:22:00.000Z"
  }
]`,
      sourceFile: 'routes/consents.js, lib/consents.js',
    },
    {
      slug: 'disclosure-log',
      method: 'GET',
      path: '/api/disclosures',
      summary: 'List FERPA §99.32 disclosures',
      description: 'Returns the FERPA §99.32 disclosure log: every recorded disclosure of student PII to a party outside the school\'s need-to-know boundary. Supports ?studentId=, ?from=, ?to= filters. Most rows are written automatically by other in-app export/transfer flows; POST /api/disclosures covers the manual-entry path for an offline disclosure.',
      auth: 'session',
      params: [
        { name: 'studentId', in: 'query', type: 'string', required: false, description: 'Filter to disclosures for one student.' },
        { name: 'from', in: 'query', type: 'string', required: false, description: 'Start date (ISO yyyy-mm-dd), inclusive.' },
        { name: 'to', in: 'query', type: 'string', required: false, description: 'End date (ISO yyyy-mm-dd), inclusive.' },
      ],
      responseExample: `[
  {
    "id": "dis_5c22",
    "studentId": "stu_2291",
    "disclosedAt": "2026-07-18T09:00:00.000Z",
    "disclosedBy": "mrivera",
    "recipient": "Lakeview Middle School registrar",
    "recipientType": "school",
    "purpose": "Student transfer — full academic record",
    "basis": "school-official",
    "dataScope": "Full academic record, IEP summary",
    "method": "pdf-export",
    "evidenceRef": null,
    "reDisclosureNotice": true,
    "createdAt": "2026-07-18T09:00:00.000Z"
  }
]`,
      sourceFile: 'routes/disclosures.js, lib/disclosures.js',
    },
    {
      slug: 'retention-policy',
      method: 'GET',
      path: '/api/admin/retention',
      summary: 'Get data retention policy config',
      description: 'Returns the tenant\'s data-retention configuration: whether automated purge is enabled (default false), per-collection retention windows in days (0 = keep forever), the allow-list of collections retention is even permitted to touch (tickets, notifications, statusIncidents only — records-of-record like users/devices/students/invoices are hardcoded ineligible). Policy is written with PUT /api/admin/retention. POST /api/admin/retention/preview does a read-only count of what would be purged. POST /api/admin/retention/run requires {"confirm":true} and enabled:true, otherwise it 400s and deletes nothing.',
      auth: 'session',
      responseExample: `{
  "enabled": false,
  "policies": {
    "tickets": { "days": 0 },
    "notifications": { "days": 90 },
    "statusIncidents": { "days": 180 }
  },
  "allowed": ["tickets", "notifications", "statusIncidents"],
  "protected": ["users", "devices", "students", "invoices", "contracts", "agreementSignatures", "audit (append-only log)", "disclosures (FERPA §99.32 log)", "disposals (student-disposal receipts)"]
}`,
      sourceFile: 'routes/retention.js, lib/retention.js',
    },
    {
      slug: 'evidence-snapshots',
      method: 'GET',
      path: '/api/admin/evidence',
      summary: 'List compliance evidence snapshots',
      description: 'Lists recent auto-captured compliance evidence snapshots, newest first (optional ?limit=, default 25, max 500). Each snapshot is a point-in-time rollup of live Suite signals — audit log activity, backup health, 2FA coverage, secret rotation staleness, security headers, retention config — assembled as counts and booleans only, never a raw secret value. A new snapshot is appended (immutably) with POST /api/admin/evidence/capture.',
      auth: 'session',
      params: [{ name: 'limit', in: 'query', type: 'number', required: false, description: 'Max snapshots to return (default 25, max 500).' }],
      responseExample: `{
  "total": 214,
  "count": 1,
  "items": [
    {
      "id": "snap_a91c",
      "createdAt": "2026-07-20T07:00:00.000Z",
      "generatedBy": "system-cron",
      "auditLog": { "entries": 18422, "latestAt": "2026-07-20T06:58:00.000Z" },
      "backups": { "tracked": 6, "healthy": 5, "stale": 1, "failed": 0 },
      "twoFactor": { "adminTotal": 8, "adminWith2fa": 8, "pctAdmin2fa": 100, "require2faAdmins": true },
      "secrets": { "catalog": 9, "configured": 6, "stale": 1, "atRestEncryption": true },
      "headers": { "hsts": true, "csp": true, "cspEnforced": true },
      "retention": { "configured": true, "enabled": false, "activePolicies": 0 }
    }
  ]
}`,
      sourceFile: 'routes/evidence-auto.js, lib/evidence-auto.js',
    },
    {
      slug: 'sla-at-risk',
      method: 'GET',
      path: '/api/sla/at-risk',
      summary: 'List tickets approaching or past SLA breach',
      description: 'Read-only scan of open help-desk tickets classified against the tenant\'s resolution SLA targets, computed in business time (a business-hours calendar, not wall clock). Returns every ticket currently at-risk or already breached, with elapsed/remaining business hours. Never mutates a ticket or raises an alert on its own — it is the same computation the scheduled scan uses to raise deduped alerts, exposed here as a plain read.',
      auth: 'session',
      responseExample: `{
  "count": 2,
  "breached": 1,
  "atRisk": 1,
  "targets": { "Low": 72, "Medium": 24, "High": 8, "Urgent": 4 },
  "tickets": [
    { "id": "tkt_5521", "title": "Wireless AP down — Building C 2nd floor", "priority": "High", "assignedTo": "mrivera", "targetHours": 8, "elapsedBusinessHours": 9.4, "remainingBusinessHours": -1.4, "breached": true, "atRisk": false, "state": "breached" },
    { "id": "tkt_5538", "title": "Projector bulb replacement — Room 214", "priority": "Medium", "assignedTo": "", "targetHours": 24, "elapsedBusinessHours": 20.1, "remainingBusinessHours": 3.9, "breached": false, "atRisk": true, "state": "at-risk" }
  ]
}`,
      sourceFile: 'routes/sla-escalation.js, lib/sla-escalation.js',
    },
  ],
};
